/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useCallback } from "react";
import Connect from "../../../utility/connect-endpoints";
import "amazon-connect-streams";
import "amazon-connect-chatjs";
import { Application } from "../components/Application/Application";
import "./AgentConsole.css";
import { Auth } from "aws-amplify";
import { getConfigValues } from "../../../sharedServices/configService";
import { initializeLayoutConfiguration } from "../services/LayoutConfig";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { eventTypes, LAYOUT_CONFIG_KEY, TEAMS_USER_ACTIVITIES } from "../../../utility/constants";
import { valToBool } from "../utility";
import { getUserByEmail } from "../services/User";
import { ButtonGroup, Button, Toast, ToastHeader } from "react-bootstrap";
import StatusAlert from "../components/StatusAlert";
import { ContactDataProvider } from "../context/contactData";
import { useWebsocketEvent } from "../../../hooks";

const COMPONENT_NAME = "Agent Console";

// globally available current user id, should only be used for logging
window.paceCurrentUserId = undefined;

// override internal ccp log level for debugging
try {
	if (valToBool(window.localStorage.getItem("trace-ccp-logs"))) {
		const rootLogger = window.connect.getLog();
		rootLogger.setLogLevel(window.connect.LogLevel.ERROR);
		rootLogger.setEchoLevel(window.connect.LogLevel.ERROR);
	}
} catch (error) {
	console.log("Could not read ccp trace log settings", error);
}

// this value is set when the agent logs in to ccp
let loggedInAgent = null;
let agentStatusWebsocket = null;
let teamsSyncEnabled = true;

const initCCP = (ccpReference) => {
	// remove existing elements
	const iframe = ccpReference.current.firstElementChild;
	if (iframe) {
		window.connect.core.terminate();
		ccpReference.removeChild(iframe);
	}
	window.connect.agentApp.initApp("ccp", "CCP", Connect.ccp, {
		ccpParams: {
			ccpUrl: Connect.ccp,
			loginPopup: true,
			loginPopupAutoClose: true,
			loginUrl: Connect.login,
			softphone: {
				allowFramedSoftphone: true,
			},
			region: `${process.env.REACT_APP_AWS_REGION}`,
		},
	});
};

const initializeApp = async (
	setInit,
	ccpReference,
	setShowTeamsStatusSyncOffline,
	setShowUserOffline,
	setAcknowledged,
	setStatus,
	setTeamsSyncHandler,
) => {
	try {
		initCCP(ccpReference);
		const config = await getConfigValues([
			"CustomerProfilesEnabled",
			"WisdomEnabled",
			LAYOUT_CONFIG_KEY,
			"SHARED_WEBSOCKET_API_URL",
			"TEAMS_USER_STATUS_SYNC_STATE",
			"TeamsSyncConfiguration",
		]);
		const layoutConfigurationResponse = {
			...config[LAYOUT_CONFIG_KEY],
			customerProfilesEnabled: config.CustomerProfilesEnabled,
			wisdomEnabled: config.WisdomEnabled,
		};
		const cognitoUser = await Auth.currentUserInfo();
		const paceUser = await getUserByEmail(cognitoUser.attributes.email);
		const enableWebsocketToSyncTeamsStatus = !!(
			config.TeamsSyncConfiguration?.TeamsSyncEnabled && // check from user management
			config.TEAMS_USER_STATUS_SYNC_STATE === "ENABLED" && // check from pipeline
			!!paceUser.isTeamsStatusSyncEnabled && //check from teams integration.
			paceUser.azureId &&
			paceUser.azureId.length
		);
		const teamsSyncConfigurationEnabled = !!(
			(config.TeamsSyncConfiguration?.TeamsSyncEnabled || config.TeamsSyncConfiguration?.connectToTeamsSyncEnabled) &&
			config.TEAMS_USER_STATUS_SYNC_STATE === "ENABLED" &&
			!!paceUser.isTeamsStatusSyncEnabled &&
			paceUser.azureId &&
			paceUser.azureId.length
		);
		let { offlineStatuses: mappedOfflineStatuses = [] } = config?.TeamsSyncConfiguration || {};
		mappedOfflineStatuses =
			mappedOfflineStatuses.length > 0 ? mappedOfflineStatuses : Object.keys(TEAMS_USER_ACTIVITIES);
		const layoutConfiguration = initializeLayoutConfiguration(layoutConfigurationResponse);
		const events = window.connect.core.getEventBus();

		//Wait for agent context before rendering CCP
		window.connect.agent(async (agent) => {
			loggedInAgent = agent;
			window.agent = loggedInAgent;

			const handleTeamsSyncMessage = (websocketData) => {
				if (!enableWebsocketToSyncTeamsStatus || !teamsSyncEnabled || !websocketData?.value) {
					return;
				}

				const teamsStatus = websocketData?.value;

				const { type } = loggedInAgent.getStatus();
				const states = loggedInAgent.getAgentStates();
				const offlineState = states.filter((state) => state.name === "Offline")[0];
				const onlineState = states.filter((state) => state.name === "Available")[0];
				let nextState = undefined;
				let showTeamsSyncMessage = false;
				if (
					type === "routable" &&
					loggedInAgent.getContacts().length === 0 &&
					mappedOfflineStatuses.includes(teamsStatus.activity)
				) {
					nextState = offlineState;
					showTeamsSyncMessage = true;
				} else if (type === "offline" && !mappedOfflineStatuses.includes(teamsStatus.activity)) {
					nextState = onlineState;
					showTeamsSyncMessage = false;
				}
				if (nextState) {
					loggedInAgent.setState(nextState, {
						success: function () {
							v2.verbose(`Set agent status to ${nextState.name}.`, {
								componentName: COMPONENT_NAME,
							});
							if (showTeamsSyncMessage) setShowTeamsStatusSyncOffline(true);
						},
						failure: function () {
							v2.warn(`Failed to set agent status to ${nextState.name}.`, {
								componentName: COMPONENT_NAME,
								logCode: errorCodes.SET_AGENT_OFFLINE_ERROR,
							});
						},
					});
				}
			};

			// important to use this syntax when setting a function
			// setTeamsSyncHandler(handleTeamsSyncMessage) will not work
			// because setState can also take in a function
			// https://beta.reactjs.org/apis/usestate#updating-state-based-on-the-previous-state
			setTeamsSyncHandler(() => handleTeamsSyncMessage);

			agent.onStateChange((agentState) => {
				const newState = agentState.agent.getStatus();
				const { type } = newState;
				setStatus(newState);
				if (type === window.connect.AgentStatusType.OFFLINE) {
					setShowUserOffline(true);
				} else {
					setShowTeamsStatusSyncOffline(false);
					setShowUserOffline(false);
				}
			});
		});

		// User logged out, reload the page
		events.subscribe(window.connect.EventType.TERMINATED, async () => {
			// eslint-disable-next-line no-restricted-globals

			const appLoadingElement = document.getElementById("App-Loading");
			const appRootElement = document.getElementById("App-Root");
			appLoadingElement.style.display = "flex";
			appRootElement.style.display = "none";
			await Auth.signOut();
		});

		// Auth error, reload the page
		events.subscribe(window.connect.EventType.AUTH_FAIL, () => {
			// eslint-disable-next-line no-restricted-globals
			location.reload(true);
		});
		events.subscribe(window.connect.EventType.ACKNOWLEDGE, () => {
			setAcknowledged(true);
		});
		setInit({ layoutConfiguration, teamsSyncConfigurationEnabled });
	} catch (error) {
		v2.error("Critical init error", {
			additionalData: { error: errorToObject(error) },
			componentName: COMPONENT_NAME,
			logCode: errorCodes.INIT_ERROR,
		});
	}
};

const AgentConsole = () => {
	const [initValues, setInitValues] = React.useState(undefined);
	const [showTeamsStatusSyncOffline, setShowTeamsStatusSyncOffline] = React.useState(false);
	const [showUserOffline, setShowUserOffline] = React.useState(false);
	const [show, setShow] = React.useState(false);
	const [message, setMessage] = React.useState("");
	const [messageType, setMessageType] = React.useState("");
	const [delay, setDelay] = React.useState(2000);
	const [expanded, setExpanded] = React.useState(false);
	const [acknowledged, setAcknowledged] = React.useState(false);
	const [status, setStatus] = React.useState({});

	const [teamsSyncHandler, setTeamsSyncHandler] = React.useState(null);

	const wsCallback = useCallback(
		async (data) => {
			if (teamsSyncHandler) {
				teamsSyncHandler(data);
			}
		},
		[teamsSyncHandler],
	);

	useWebsocketEvent(eventTypes.TEAMS_STATUS_UPDATE, wsCallback);

	const toggleTeamsSync = React.useCallback((newTeamsSetting) => {
		teamsSyncEnabled = newTeamsSetting;
		if (!newTeamsSetting) {
			setShowTeamsStatusSyncOffline(false);
		}
	}, []);

	const ccpReference = React.useRef(undefined);

	React.useEffect(() => {
		initializeApp(
			setInitValues,
			ccpReference,
			setShowTeamsStatusSyncOffline,
			setShowUserOffline,
			setAcknowledged,
			setStatus,
			setTeamsSyncHandler,
		);
		return () => {
			if (loggedInAgent) {
				if (agentStatusWebsocket) {
					agentStatusWebsocket.close();
					agentStatusWebsocket = null;
				}
				const states = loggedInAgent.getAgentStates();
				const offlineState = states.filter((state) => state.name === "Offline")[0];

				loggedInAgent.setState(offlineState, {
					success: function () {
						v2.verbose("Set agent status to offline.", {
							componentName: COMPONENT_NAME,
						});
					},
					failure: function () {
						v2.warn("Failed to set agent status to offline.", {
							componentName: COMPONENT_NAME,
							logCode: errorCodes.SET_AGENT_OFFLINE_ERROR,
						});
					},
				});
			}
			window.connect.core.terminate();
		};
	}, []);
	const toastNotification = React.useCallback((show, message, messageType, delay) => {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					position: "absolute",
					bottom: "20px",
					left: 0,
					right: 0,
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				<div style={{ position: "relative", margin: "auto" }}>
					<Toast
						style={{
							width: "max-content",
						}}
						className={"bg-" + messageType}
						onClose={() => setShow(false)}
						show={show}
						delay={delay}
						autohide
					>
						<ToastHeader style={{ border: "none" }}>
							<strong className="me-auto">{message}</strong>
						</ToastHeader>
					</Toast>
				</div>
			</div>
		);
	}, []);
	const renderToastContent = (show, message, messageType, delay = 2000) => {
		setDelay(delay);
		setShow(show);
		setMessage(message);
		setMessageType(messageType);
	};

	return (
		<>
			{showTeamsStatusSyncOffline || showUserOffline ? (
				<div id="Warning-Messages">
					{showTeamsStatusSyncOffline && (
						<div>
							Your agent status has been set to offline due to your current availability status on Microsoft Teams.
						</div>
					)}
					{showUserOffline && (
						<div>
							Your agent status appears to be offline, to start receiving incoming communication set your status to
							available.
						</div>
					)}
				</div>
			) : (
				<div style={{ margin: "12px" }} />
			)}
			<div style={{ display: "flex", height: "90vh" }}>
				<div
					style={{
						minWidth: "490px",
						width: expanded ? "100%" : "30%",
						display: acknowledged && window.connect?.agent ? "flex" : "none",
						flexDirection: "column",
					}}
				>
					<div
						ref={ccpReference}
						style={{
							display: "block",
						}}
						id={"CCP"}
					/>
					{initValues && (
						<div>
							<div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
								{initValues.teamsSyncConfigurationEnabled ? (
									<div style={{ display: "flex" }}>
										<div style={{ margin: "auto 10px", display: "inline" }}>Sync Teams Status</div>
										<div>
											<ButtonGroup size="sm" toggle>
												<Button
													size="sm"
													variant={"outline-secondary"}
													onClick={() => {
														if (!teamsSyncEnabled) {
															toggleTeamsSync(true);
															renderToastContent(true, "Team Status Sync Enabled", "success");
														}
													}}
													active={teamsSyncEnabled}
												>
													Enable
												</Button>
												<Button
													size="sm"
													variant={"outline-secondary"}
													onClick={() => {
														if (teamsSyncEnabled) {
															toggleTeamsSync(false);
															renderToastContent(true, "Team Status Sync Disabled", "success");
														}
													}}
													active={!teamsSyncEnabled}
												>
													Disable
												</Button>
											</ButtonGroup>
										</div>
									</div>
								) : (
									<div />
								)}
								<div>
									<Button size="sm" variant={"outline-secondary"} onClick={() => setExpanded(!expanded)}>
										{expanded ? "Collapse" : "Expand"}
									</Button>
								</div>
							</div>
						</div>
					)}
				</div>
				{acknowledged && (
					<ContactDataProvider>
						<Application
							layoutConfiguration={initValues.layoutConfiguration}
							renderToastContent={renderToastContent}
							teamsSyncEnabled={teamsSyncEnabled}
						/>
					</ContactDataProvider>
				)}
				{!acknowledged && (
					<div style={{ left: "50%", position: "fixed", top: "50%", display: "block" }}>
						<div>
							<i className="fas fa-spinner loading-spinner spin-animation" />
						</div>
						<div id="CCP-Loading" style={{ top: "50px", position: "relative", left: "-200px" }}>
							<em id="CCP-Login-Message" style={{ textAlign: "center" }}>
								Please enable pop-ups to login. If you do not see a pop-up
								<div>
									<a id="CCP-Login-Link" href={window.location}>
										click here
									</a>
									{" to reload the page."}
								</div>
							</em>
						</div>
					</div>
				)}
			</div>
			{toastNotification(show, message, messageType, delay)}
			<StatusAlert status={status} />
		</>
	);
};

export default AgentConsole;
